import { decodeGsxBlock } from "../../format/decoder";
import type { GsxMeta, SplatBlock } from "../../format/format";
import { getCachedBytes, putCachedBytes } from "../../cache";
import type { GsplatTransport } from "../../transport/types";

export interface SceneLoadProgress {
  /** "cache" | "fetch" | "decode" | "addNode" | "ready" | "ready (cached)" */
  stage: string;
  splatCount: number;
}

export interface SceneLoaderOptions {
  onProgress?: (p: SceneLoadProgress) => void;
  onError?: (err: { stage: string; message: string; cause?: string }) => void;
}

interface RendererSink {
  addNode(nodeId: number, block: SplatBlock): boolean;
}

/**
 * Single-block scene loader. .gsx file with exactly one block holding all
 * splats. Fetches → decodes → pushes to renderer in one shot.
 *
 * `cacheKey` (e.g. job id) is used as the IndexedDB key for raw GSX bytes.
 */
export class SceneLoader {
  private cacheKey: string;
  private transport: GsplatTransport;
  private renderer: RendererSink;
  private opts: SceneLoaderOptions;
  private meta: GsxMeta | null = null;
  private aborted = false;

  constructor(
    cacheKey: string,
    transport: GsplatTransport,
    renderer: RendererSink,
    opts: SceneLoaderOptions = {},
  ) {
    this.cacheKey = cacheKey;
    this.transport = transport;
    this.renderer = renderer;
    this.opts = opts;
  }

  async loadMeta(): Promise<GsxMeta> {
    const meta = await this.transport.getMeta();
    this.meta = meta;
    return meta;
  }

  hasMeta(): boolean {
    return this.meta !== null;
  }

  getMeta(): GsxMeta | null {
    return this.meta;
  }

  abort(): void {
    this.aborted = true;
  }

  async loadScene(): Promise<void> {
    if (!this.meta || this.aborted) return;
    const meta = this.meta;
    if (meta.blocks.length === 0 || meta.header.splatCount === 0) return;
    const entry = meta.blocks[0]!;
    let stage = "cache";
    try {
      this.opts.onProgress?.({ stage, splatCount: 0 });
      let bytes = await getCachedBytes(this.cacheKey);
      let fromCache = false;
      if (bytes && bytes.byteLength === entry.sizeCompressed) {
        fromCache = true;
        // eslint-disable-next-line no-console
        console.log(`[scene] cache hit ${this.cacheKey} (${bytes.byteLength} B)`);
      } else {
        if (bytes) {
          // eslint-disable-next-line no-console
          console.warn(
            `[scene] cached size ${bytes.byteLength} != meta ${entry.sizeCompressed}, refetching`,
          );
        }
        stage = "fetch";
        this.opts.onProgress?.({ stage, splatCount: 0 });
        bytes = await this.transport.getBlockBytes(0, {
          offset: entry.offset,
          sizeCompressed: entry.sizeCompressed,
        });
        if (this.aborted) return;
        void putCachedBytes(this.cacheKey, bytes);
      }
      stage = "decode";
      this.opts.onProgress?.({ stage, splatCount: 0 });
      const block = await decodeGsxBlock(bytes, meta.header, entry);
      if (this.aborted) return;
      stage = "addNode";
      const ok = this.renderer.addNode(0, block);
      if (!ok) {
        throw new Error(
          `renderer rejected block (${block.splatCount} splats > GPU capacity)`,
        );
      }
      this.opts.onProgress?.({
        stage: fromCache ? "ready (cached)" : "ready",
        splatCount: block.splatCount,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const causeMsg =
        err instanceof Error && err.cause instanceof Error ? err.cause.message : undefined;
      this.opts.onError?.({ stage, message: msg, cause: causeMsg });
    }
  }
}
